import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isNotNullish, isNullish } from '@examdojo/core/util/nullish';
import {
  PracticeActivityQuery,
  PracticeActivityService,
  PracticeActivityStoreModel,
} from '@examdojo/practice-activity';
import {
  QuestionAttemptEntityQuery,
  QuestionAttemptPendingCreationService,
  QuestionAttemptStoreModel,
} from '@examdojo/question';
import { debounceTime, distinctUntilChanged, distinctUntilKeyChanged, filter, map, pairwise, switchMap } from 'rxjs';

@Injectable()
export class FetchAttemptWhenMismatchEffectService {
  constructor(
    private readonly practiceActivityQuery: PracticeActivityQuery,
    private readonly practiceActivityService: PracticeActivityService,
    private readonly pendingCreationService: QuestionAttemptPendingCreationService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
  ) {
    this.syncMismatchedAttempts().pipe(takeUntilDestroyed()).subscribe();
  }

  private syncMismatchedAttempts() {
    return this.practiceActivityQuery.inProgressPracticeActivityId$.pipe(
      filter(isNotNullish),
      distinctUntilChanged(),
      switchMap((activityId) => this.watchActivityChanges(activityId)),
    );
  }

  private watchActivityChanges(activityId: string) {
    return this.practiceActivityQuery.selectEntity(activityId).pipe(
      filter(isNotNullish),
      distinctUntilKeyChanged<PracticeActivityStoreModel>('question_attempt_id'),
      pairwise(),
      switchMap(([oldActivity, newActivity]) => this.handleActivityChange(oldActivity, newActivity)),
    );
  }

  private handleActivityChange(oldActivity: PracticeActivityStoreModel, newActivity: PracticeActivityStoreModel) {
    return this.questionAttemptQuery.active$.pipe(
      map((entity) => entity?.attempt),
      filter(isNotNullish),
      debounceTime(0),
      map((attempt) => this.createMismatchContext(oldActivity, newActivity, attempt)),
      distinctUntilChanged((a, b) => a.isMismatched === b.isMismatched && a.attempt.id === b.attempt.id),
      filter(({ isMismatched, attempt }) => isMismatched && attempt.status !== 'SUBMITTED'),
      filter((context) => this.shouldProcessMismatch(context)),
      switchMap(({ activity, activityQuestionAttemptId }) =>
        this.practiceActivityService.fetchQuestionAttempts(activityQuestionAttemptId, activity.id),
      ),
    );
  }

  private createMismatchContext(
    prevActivity: PracticeActivityStoreModel,
    activity: PracticeActivityStoreModel,
    attempt: QuestionAttemptStoreModel,
  ) {
    return {
      prevActivity,
      activity,
      attempt,
      activityQuestionAttemptId: activity.question_attempt_id!,
      isMismatched: activity.question_attempt_id !== attempt.id,
    };
  }

  private shouldProcessMismatch({
    prevActivity,
    activity,
  }: {
    prevActivity: PracticeActivityStoreModel;
    activity: PracticeActivityStoreModel;
  }): boolean {
    if (isNullish(prevActivity.question_attempt_id)) {
      return false;
    }

    const pendingCreation = this.pendingCreationService.getPendingCreation(prevActivity.question_attempt_id);
    const isPending =
      !!pendingCreation && pendingCreation.stage === 'recreate_triggered' && isNotNullish(activity.question_attempt_id);

    if (isPending) {
      this.pendingCreationService.clearPendingCreation(prevActivity.question_attempt_id);
    }

    return !isPending;
  }
}
